/**
 * Input + DayPicker popover wrapper for react-day-picker v9.
 * Replaces the removed DayPickerInput from v7; keeps a similar API for migration.
 */
import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { DayPicker } from 'react-day-picker'
import { format, parse, isValid } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { zhCN } from 'date-fns/locale'
import { dayPickerConfig } from 'utils/time'
import 'react-day-picker/style.css'

const localeMap = { 'zh-cn': zhCN, en: enUS }

export const formatDateForPicker = (date, formatStr, locale) => {
  const loc = locale ? localeMap[locale] || enUS : enUS
  return format(date, formatStr, { locale: loc })
}

export const parseDateForPicker = (str, formatStr, locale) => {
  if (!str || !str.trim()) return undefined
  const loc = locale ? localeMap[locale] || enUS : enUS
  const trimmed = str.trim()
  const formats = [formatStr]
  // Accept both "Jul" (MMM) and "July" (MMMM) when typing manually
  if (formatStr?.includes('MMMM')) {
    formats.push(formatStr.replace('MMMM', 'MMM'))
  } else if (formatStr?.includes('MMM')) {
    formats.push(formatStr.replace('MMM', 'MMMM'))
  }
  for (const f of formats) {
    if (!f) continue
    const parsed = parse(trimmed, f, new Date(), { locale: loc })
    if (isValid(parsed)) return parsed
  }
  return undefined
}

const hasFullYearInInput = (str, formatStr) => {
  if (!str?.trim() || !/y{3,4}/.test(formatStr || '')) return true
  const trimmed = str.trim()
  if (/^y/i.test(formatStr)) return /^\d{4}/.test(trimmed)
  if (/y{3,4}$/i.test(formatStr)) return /\d{4}$/.test(trimmed)
  return true
}

const DayPickerInput = forwardRef(function DayPickerInput(
  {
    value,
    onDayChange,
    onChangeRaw,
    format: formatStr,
    placeholder,
    dayPickerProps = {},
    overlayComponent: OverlayComponent,
    inputProps = {},
    classNames = {},
    disabled,
    deferParseUntilFullYear = true,
    preserveInputWhileFocused = true,
  },
  ref
) {
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const fmt = formatStr || dayPickerConfig.formatDateFns || 'dd MMM yyyy'
  const loc = dayPickerConfig.locale ? localeMap[dayPickerConfig.locale] || enUS : enUS

  const valueDate = value instanceof Date ? value : value ? parseDateForPicker(String(value), fmt, dayPickerConfig.locale) : undefined
  const [selectedDate, setSelectedDate] = useState(valueDate)
  const [inputValue, setInputValue] = useState(
    valueDate ? formatDateForPicker(valueDate, fmt, dayPickerConfig.locale) : ''
  )
  const [month, setMonth] = useState(valueDate || new Date())
  const lastValueTimeRef = useRef(valueDate ? valueDate.getTime() : null)
  const isEditingRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const onMouseDown = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [isOpen])

  useEffect(() => {
    if (preserveInputWhileFocused && isEditingRef.current) return

    const nextDate =
      value instanceof Date
        ? value
        : value
          ? parseDateForPicker(String(value), fmt, dayPickerConfig.locale)
          : undefined
    const nextTime = nextDate ? nextDate.getTime() : null
    const didValueChange = nextTime !== lastValueTimeRef.current

    setSelectedDate(nextDate)
    setInputValue(nextDate ? formatDateForPicker(nextDate, fmt, dayPickerConfig.locale) : '')

    if (didValueChange && nextDate) setMonth(nextDate)
    lastValueTimeRef.current = nextTime
  }, [value, fmt, preserveInputWhileFocused])

  useImperativeHandle(ref, () => ({
    getInput: () => inputRef.current,
    handleInputChange: ev => {
      handleInputChange(ev)
    },
  }))

  const handleSelect = date => {
    setSelectedDate(date)
    setInputValue(date ? formatDateForPicker(date, fmt, dayPickerConfig.locale) : '')
    if (date) setMonth(date)
    if (typeof onDayChange === 'function') {
      onDayChange(date || null, undefined, {
        input: { value: date ? formatDateForPicker(date, fmt, dayPickerConfig.locale) : '' },
      })
    }
    onSelectProp?.(date)
    if (date) setIsOpen(false)
  }

  const applyInputChange = v => {
    setInputValue(v)
    if (deferParseUntilFullYear && !hasFullYearInInput(v, fmt)) {
      // Keep parent/calendar stable while the year is still being typed
      return
    }
    const parsed = parseDateForPicker(v, fmt, dayPickerConfig.locale)
    setSelectedDate(parsed || undefined)
    if (parsed) setMonth(parsed)
    if (typeof onDayChange === 'function') onDayChange(parsed || null, undefined, { input: { value: v } })
  }

  const handleInputChange = e => {
    if (typeof onChangeRaw === 'function') onChangeRaw(e)
    applyInputChange(e.target.value)
  }

  const handleBlur = e => {
    if (preserveInputWhileFocused) isEditingRef.current = false
    if (!containerRef.current?.contains(e.relatedTarget)) setIsOpen(false)
    restInputProps.onBlur?.(e)
  }

  const openCalendar = () => {
    if (preserveInputWhileFocused) isEditingRef.current = true
    setIsOpen(true)
  }

  const handleContainerClick = e => {
    if (e.target.closest('.DayPickerInput-OverlayWrapper') || e.target.closest('.DayPickerInput-Overlay')) {
      return
    }
    inputRef.current?.focus()
    openCalendar()
  }

  const disabledMatcher = dayPickerProps.disabledDays || dayPickerProps.disabled || disabled
  // Caller `month` must not override local month (breaks nav + typed-date calendar sync)
  const { selectedRange, onSelect: onSelectProp, month: _monthProp, ...dayPickerRest } = dayPickerProps
  const { ref: inputRefProp, ...restInputProps } = inputProps || {}

  const calendar = (
    <DayPicker
      mode="single"
      selected={selectedDate}
      onSelect={handleSelect}
      month={month}
      onMonthChange={setMonth}
      locale={loc}
      disabled={disabledMatcher}
      {...dayPickerRest}
    />
  )

  const selectedDayForOverlay = selectedRange ? [selectedDate, selectedRange] : [selectedDate]
  const overlayContent = OverlayComponent ? (
    <OverlayComponent classNames={classNames} selectedDay={selectedDayForOverlay} selectedRange={selectedRange}>
      {calendar}
    </OverlayComponent>
  ) : (
    <div className={classNames.overlayWrapper || 'DayPickerInput-OverlayWrapper'}>
      <div className={classNames.overlay || 'DayPickerInput-Overlay'}>{calendar}</div>
    </div>
  )

  return (
    <div
      ref={containerRef}
      className={classNames.container || 'DayPickerInput'}
      onClick={handleContainerClick}>
      <input
        ref={el => {
          inputRef.current = el
          if (inputRefProp) {
            if (typeof inputRefProp === 'function') inputRefProp(el)
            else inputRefProp.current = el
          }
        }}
        type="text"
        value={inputValue}
        placeholder={placeholder ?? dayPickerConfig.placeholder}
        {...restInputProps}
        onChange={handleInputChange}
        onFocus={e => {
          openCalendar()
          restInputProps.onFocus?.(e)
        }}
        onBlur={handleBlur}
      />
      {isOpen && (
        <div onMouseDown={e => e.preventDefault()}>
          {overlayContent}
        </div>
      )}
    </div>
  )
})

DayPickerInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onDayChange: PropTypes.func,
  onChangeRaw: PropTypes.func,
  format: PropTypes.string,
  placeholder: PropTypes.string,
  dayPickerProps: PropTypes.object,
  overlayComponent: PropTypes.elementType,
  inputProps: PropTypes.object,
  classNames: PropTypes.object,
  disabled: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  deferParseUntilFullYear: PropTypes.bool,
  preserveInputWhileFocused: PropTypes.bool,
}

export default DayPickerInput
